import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Clock, Calendar, User, FileText, Pill, Heart, Users, Phone,
  Activity, AlertCircle, CheckCircle, ChevronRight, Filter
} from "lucide-react";

const EVENT_ICONS = {
  visit: Calendar,
  medication: Pill,
  assessment: Activity,
  note: FileText,
  care_plan: Heart,
  family_communication: Users
};

const EVENT_COLORS = {
  visit: "bg-blue-100 text-blue-700 border-blue-200",
  medication: "bg-purple-100 text-purple-700 border-purple-200",
  assessment: "bg-green-100 text-green-700 border-green-200",
  note: "bg-gray-100 text-gray-700 border-gray-200",
  care_plan: "bg-indigo-100 text-indigo-700 border-indigo-200",
  family_communication: "bg-orange-100 text-orange-700 border-orange-200"
};

export default function CareTimeline({ patientId: propPatientId }) {
  const params = useParams();
  const patientId = propPatientId || params.patientId;
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [expandedEvents, setExpandedEvents] = useState(new Set());

  useEffect(() => {
    if (patientId) {
      loadTimeline();
    }
  }, [patientId]);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/phase24/timeline/${patientId}`);
      setTimeline(response.data);
    } catch (error) {
      console.error("Failed to load timeline:", error);
      toast.error("Failed to load care timeline");
    } finally {
      setLoading(false);
    }
  };

  const toggleEventExpanded = (eventId) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const filteredEvents = timeline?.events?.filter(event => {
    if (filterType === "all") return true;
    return event.type === filterType;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="timeline-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!timeline) {
    return (
      <Card data-testid="timeline-empty">
        <CardContent className="py-12 text-center">
          <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No timeline data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="care-timeline">
      {/* Patient Summary Header */}
      <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-teal-900">{timeline.patient_name}</h2>
              <p className="text-teal-700">Care Timeline</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="text-center px-4 py-2 bg-white/50 rounded-lg">
                <p className="text-2xl font-bold text-teal-800">{timeline.summary?.days_in_care || 0}</p>
                <p className="text-xs text-teal-600">Days in Care</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/50 rounded-lg">
                <p className="text-2xl font-bold text-teal-800">{timeline.summary?.visit_count || 0}</p>
                <p className="text-xs text-teal-600">Total Visits</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/50 rounded-lg">
                <p className="text-2xl font-bold text-teal-800">{timeline.summary?.assessments_completed || 0}</p>
                <p className="text-xs text-teal-600">Assessments</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/50 rounded-lg">
                <p className="text-2xl font-bold text-teal-800">{timeline.summary?.medication_changes || 0}</p>
                <p className="text-xs text-teal-600">Med Changes</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Care Phases */}
      {timeline.phases?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Care Journey Phases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {timeline.phases.map((phase, index) => (
                <div key={phase.id} className="flex items-center">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                    phase.status === "completed" ? "bg-green-100 text-green-700" :
                    phase.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {phase.status === "completed" ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : phase.status === "in_progress" ? (
                      <Activity className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    <span className="font-medium whitespace-nowrap">{phase.name}</span>
                  </div>
                  {index < timeline.phases.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-gray-300 mx-1" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestones */}
      {timeline.milestones?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Key Milestones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {timeline.milestones.map((milestone) => (
                <Badge
                  key={milestone.id}
                  variant={milestone.achieved ? "default" : "outline"}
                  className={milestone.achieved ? "bg-green-100 text-green-800" : ""}
                >
                  {milestone.achieved && <CheckCircle className="w-3 h-3 mr-1" />}
                  {milestone.title}
                  {milestone.date && (
                    <span className="ml-1 opacity-75">
                      ({new Date(milestone.date).toLocaleDateString()})
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline Events */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Timeline Events</CardTitle>
              <CardDescription>{filteredEvents.length} events</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={filterType === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterType("all")}
              >
                All
              </Button>
              {["visit", "medication", "assessment", "note", "care_plan"].map((type) => {
                const IconComponent = EVENT_ICONS[type] || Calendar;
                return (
                  <Button
                    key={type}
                    variant={filterType === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterType(type)}
                    className="capitalize"
                  >
                    <IconComponent className="w-3 h-3 mr-1" />
                    {type.replace("_", " ")}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

              {/* Events */}
              <div className="space-y-4">
                {filteredEvents.map((event, index) => {
                  const IconComponent = EVENT_ICONS[event.type] || Calendar;
                  const colorClass = EVENT_COLORS[event.type] || EVENT_COLORS.note;
                  const isExpanded = expandedEvents.has(event.id);

                  return (
                    <div
                      key={event.id}
                      className="relative pl-14"
                      data-testid={`timeline-event-${event.id}`}
                    >
                      {/* Event icon */}
                      <div className={`absolute left-2 w-8 h-8 rounded-full flex items-center justify-center border-2 ${colorClass}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>

                      {/* Event card */}
                      <div
                        className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${colorClass}`}
                        onClick={() => toggleEventExpanded(event.id)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{event.title}</h4>
                              <Badge variant="outline" className="text-xs">
                                {event.category}
                              </Badge>
                            </div>
                            <p className={`text-sm ${isExpanded ? "" : "line-clamp-2"}`}>
                              {event.description}
                            </p>
                          </div>
                          <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                        </div>

                        {/* Expanded details */}
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-current/10 space-y-2">
                            {event.staff_name && (
                              <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4" />
                                <span>{event.staff_name}</span>
                              </div>
                            )}
                            {event.duration && (
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="w-4 h-4" />
                                <span>{event.duration} minutes</span>
                              </div>
                            )}
                            {event.score !== undefined && (
                              <div className="flex items-center gap-2 text-sm">
                                <Activity className="w-4 h-4" />
                                <span>Score: {event.score}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Timestamp */}
                        <div className="flex items-center gap-1 mt-2 text-xs opacity-75">
                          <Clock className="w-3 h-3" />
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredEvents.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No events found for the selected filter</p>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
